// ============================================================================
// view-transform.ts — transformation de VUE (pan / zoom / rotation) d'une figure.
// PUR & testable. N'altère JAMAIS les coordonnées de la figure : c'est un calque
// d'affichage appliqué à un <g transform> au-dessus de la projection.
// Distances/positions en pixels de l'espace utilisateur SVG (le viewBox).
// ============================================================================

export interface ViewTransform {
  /** Échelle (zoom). */
  k: number;
  /** Translation (pan) en px viewBox. */
  tx: number;
  ty: number;
  /** Rotation en radians. */
  rot: number;
}

export const IDENTITY_VIEW: ViewTransform = { k: 1, tx: 0, ty: 0, rot: 0 };

const MIN_K = 0.3;
const MAX_K = 8;
export const clampScale = (k: number): number => Math.min(MAX_K, Math.max(MIN_K, k));

const r3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Construit l'attribut `transform` SVG : pan, puis rotation+échelle autour du centre. */
export function viewToTransform(v: ViewTransform, cx: number, cy: number): string {
  const deg = (v.rot * 180) / Math.PI;
  return (
    `translate(${r3(v.tx)} ${r3(v.ty)}) ` +
    `rotate(${r3(deg)} ${cx} ${cy}) ` +
    `translate(${cx} ${cy}) scale(${r3(v.k)}) translate(${-cx} ${-cy})`
  );
}

export function panBy(v: ViewTransform, dx: number, dy: number): ViewTransform {
  return { ...v, tx: v.tx + dx, ty: v.ty + dy };
}

/** Zoom multiplicatif (autour du centre du viewBox), borné. */
export function zoomBy(v: ViewTransform, factor: number): ViewTransform {
  return { ...v, k: clampScale(v.k * factor) };
}

export function rotateBy(v: ViewTransform, deltaRad: number): ViewTransform {
  return { ...v, rot: v.rot + deltaRad };
}

/** Instantané au moment où le 2ᵉ doigt se pose (gesture pinch+rotation). */
export interface PinchStart {
  /** Distance entre les deux doigts (px viewBox). */
  dist: number;
  /** Angle du segment entre les deux doigts (radians). */
  angle: number;
  /** Centroïde des deux doigts (px viewBox). */
  cx: number;
  cy: number;
  /** Vue au début du geste. */
  view: ViewTransform;
}

/**
 * Applique un geste à deux doigts : zoom = ratio des distances, rotation = delta
 * d'angle, pan = delta du centroïde — le tout relatif à l'instantané de départ
 * (pas de dérive). Tout en px viewBox.
 */
export function applyPinch(
  start: PinchStart,
  dist: number,
  angle: number,
  cx: number,
  cy: number,
): ViewTransform {
  const ratio = start.dist > 1e-6 ? dist / start.dist : 1;
  return {
    k: clampScale(start.view.k * ratio),
    rot: start.view.rot + (angle - start.angle),
    tx: start.view.tx + (cx - start.cx),
    ty: start.view.ty + (cy - start.cy),
  };
}

/** Distance, angle (radians) et centroïde de deux points. */
export function pinchMetrics(
  a: { x: number; y: number },
  b: { x: number; y: number },
): { dist: number; angle: number; cx: number; cy: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return {
    dist: Math.hypot(dx, dy),
    angle: Math.atan2(dy, dx),
    cx: (a.x + b.x) / 2,
    cy: (a.y + b.y) / 2,
  };
}
